import React, { useEffect, useState } from "react";
import { Button, Form, Empty } from "antd";
import { TableOutlined, PlusOutlined } from "@ant-design/icons";
import useMenu from "../hooks/useMenu";
import { nowDate } from "../components/Customs/nowDate";

import { GeneralService } from "../services/GeneralService";
import { notify } from "../components/Customs/NotifyProvider";

import ListTable from "../components/table/ListTable";
import CreateTable from "../components/table/CreateTable";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const MODULE_KEY = "manage_table";

const ManageTable = () => {
  const { loading, createMenu, updateMenu, zone } = useMenu();
  const [form] = Form.useForm();

  const [fC, setFC] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);

  const openCreate = () => {
    if (!canCreateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to create table");
      return;
    }

    setEditingMenu(null);
    setDrawerOpen(true);
  };

  const openEdit = (menu) => {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update table");
      return;
    }

    setEditingMenu(menu);
    setDrawerOpen(true);
  };

  const fetchFC = async () => {
    if (!canViewModule(MODULE_KEY)) return;

    try {
      const data = await GeneralService.get("tables");
      const allTables = Array.isArray(data?.data) ? data.data : [];

      // Hide the hidden per-room backing tables Room Service creates
      // (see roomTableLink.js) — they're not real restaurant tables to
      // manage here, just internal plumbing so room bills can reuse the
      // billing engine.
      const realTables = allTables.filter(
        (t) => !String(t.name || "").startsWith("RoomSvc-")
      );

      setFC(realTables);
    } catch (error) {
      notify.error(error?.message || "Failed to fetch tables");
      setFC([]);
    }
  };

  const deleteMenu = async (id) => {
    if (!canDeleteModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to delete table");
      return;
    }

    try {
      const data = await GeneralService.delete("tables", id);

      if (data?.status) {
        notify.success(data?.message || "Table deleted successfully");
      } else {
        notify.error(data?.message || "Failed to delete table");
      }

      fetchFC();
    } catch (error) {
      notify.error(error?.message || "Failed to delete table");
      fetchFC();
    }
  };

  useEffect(() => {
    fetchFC();
  }, []);

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view tables" />
        </div>
      </div>
    );
  }

  const handleSubmit = async (values) => {
    const req = { ...values, added_on: nowDate(), updated_on: nowDate() };
    const reqUpdate = { ...values, updated_on: nowDate() };

    if (editingMenu) {
      if (!canUpdateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to update table");
        return;
      }

      await updateMenu("tables", editingMenu.id, reqUpdate);
      fetchFC();
    } else {
      if (!canCreateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to create table");
        return;
      }

      await createMenu("tables", req);
      fetchFC();
    }

    setDrawerOpen(false);
    form.resetFields();
  };

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <TableOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Manage Table</h2>
            <p className="user-page-subtitle">
              Create, update, and manage restaurant tables from one place.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            type="primary"
            className="primary-btn"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Create Table
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Table List</h3>
            <p>View and manage all available tables.</p>
          </div>
        </div>

        <ListTable
          data={fC}
          loading={loading}
          onEdit={openEdit}
          onDelete={deleteMenu}
          zone={zone}
          canUpdate={canUpdateModule(MODULE_KEY)}
          canDelete={canDeleteModule(MODULE_KEY)}
        />
      </div>

      <CreateTable
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          form.resetFields();
        }}
        onSubmit={handleSubmit}
        initialValues={editingMenu}
        form={form}
        zone={zone}
      />
    </div>
  );
};

export default ManageTable;